import config from "../config.json"

import Cache from "./Cache"
import RandomNumberGenerator from "./RandomNumberGenerator"
import GameGenerator from "./GameGenerator"
import FighterGenerator from "./FighterGenerator"
import NumberValueGenerator from "./ValueGenerator/NumberValueGenerator"
import ProductValueGenerator from "./ValueGenerator/ProductValueGenerator"
import DivisionValueGenerator from "./ValueGenerator/DivisionValueGenerator"
import FightResolver from "./FightResolver"
import SumValueGenerator from "./ValueGenerator/SumValueGenerator"
import GameDeserializer from "./GameDeserializer"
import BracketedValueDeserializer from "./ValueDeserializer/BracketedValueDeserializer"
import DivisionValueDeserializer from "./ValueDeserializer/DivisionValueDeserializer"
import NumberValueDeserializer from "./ValueDeserializer/NumberValueDeserializer"
import ProductValueDeserializer from "./ValueDeserializer/ProductValueDeserializer"
import NumberGenerator from "./NumberGenerator"
import FactorialValueDeserializer from "./ValueDeserializer/FactorialValueDeserializer"
import FactorialValueGenerator from "./ValueGenerator/FactorialValueGenerator"
import ConstantValueGenerator, {
  ConstantDefinition,
} from "./ValueGenerator/ConstantValueGenerator"
import ConstantValueDeserializer from "./ValueDeserializer/ConstantValueDeserializer"
import SubtractionValueGenerator from "./ValueGenerator/SubtractionValueGenerator"
import SubtractionValueDeserializer from "./ValueDeserializer/SubtractionValueDeserializer"
import EquationValueGenerator from "./ValueGenerator/EquationValueGenerator"
import SumEquationValueGenerator from "./ValueGenerator/EquationGenerator/SumEquationValueGenerator"
import SubtractionEquationValueGenerator from "./ValueGenerator/EquationGenerator/SubtractionEquationValueGenerator"
import ProductEquationValueGenerator from "./ValueGenerator/EquationGenerator/ProductEquationValueGenerator"
import DivisionEquationValueGenerator from "./ValueGenerator/EquationGenerator/DivisionEqualtionValueGenerator"
import ExpressionValueGenerator from "./ValueGenerator/ExpressionValueGenerator"
import ProductExpressionValueGenerator from "./ValueGenerator/ExpressionValueGenerator/ProductExpressionValueGenerator"
import DivisionExpressionValueGenerator from "./ValueGenerator/ExpressionValueGenerator/DivisionExpressionValueGenerator"
import Logger from "./Logger"
import ValueGeneratorInterface from "./ValueGenerator/ValueGeneratorInterface"
import GameProvider from "./GameProvider"

export const cache = new Cache(config.cachePrefix)
const logger: Logger = new Logger(config.debug)
export const randomNumberGenerator: RandomNumberGenerator =
  new RandomNumberGenerator(
    config.debug ? "1337" : new Date().toDateString(),
    // new Date().toTimeString(),
  )

export const constantValueDefinitions: ConstantDefinition[] = [
  {
    // https://en.wikipedia.org/wiki/Pi
    value: Math.PI,
    symbol: "π",
    range: [4, 4],
  },
  {
    // https://en.wikipedia.org/wiki/Golden_ratio
    value: (1 + Math.sqrt(5)) / 2,
    symbol: "φ",
    range: [1, 2],
  },
  {
    // https://en.wikipedia.org/wiki/E_(mathematical_constant)
    value: Math.E,
    symbol: "e",
    range: [3, 3],
  },
]

export const numberValueGenerator: NumberValueGenerator =
  new NumberValueGenerator()
export const sumValueGenerator: SumValueGenerator = new SumValueGenerator(
  randomNumberGenerator,
)
export const productValueGenerator: ProductValueGenerator =
  new ProductValueGenerator(randomNumberGenerator)
export const divisionValueGenerator: DivisionValueGenerator =
  new DivisionValueGenerator(randomNumberGenerator)
export const subtractionValueGenerator: SubtractionValueGenerator =
  new SubtractionValueGenerator(randomNumberGenerator)
export const factorialValueGenerator: FactorialValueGenerator =
  new FactorialValueGenerator()
export const constantValueGenerator: ConstantValueGenerator =
  new ConstantValueGenerator(constantValueDefinitions)
export const sumEquationValueGenerator: SumEquationValueGenerator =
  new SumEquationValueGenerator(randomNumberGenerator)
export const subtractionEquationValueGenerator: SubtractionEquationValueGenerator =
  new SubtractionEquationValueGenerator(randomNumberGenerator)
export const productEquationValueGenerator: ProductEquationValueGenerator =
  new ProductEquationValueGenerator(randomNumberGenerator)
export const divisionEquationValueGenerator: DivisionEquationValueGenerator =
  new DivisionEquationValueGenerator(randomNumberGenerator)

export const expressionValueGenerators: ValueGeneratorInterface[] = [
  // a * (b + c)
  new ProductExpressionValueGenerator(
    randomNumberGenerator,
    numberValueGenerator,
    sumValueGenerator,
    "right",
  ),
  // a * (b - c)
  new ProductExpressionValueGenerator(
    randomNumberGenerator,
    numberValueGenerator,
    subtractionValueGenerator,
    "right",
  ),
  // (a - b) * c
  new ProductExpressionValueGenerator(
    randomNumberGenerator,
    subtractionValueGenerator,
    numberValueGenerator,
    "left",
  ),
  // (a + b) * c
  new ProductExpressionValueGenerator(
    randomNumberGenerator,
    sumValueGenerator,
    numberValueGenerator,
    "left",
  ),
  // a * b * c
  new ProductExpressionValueGenerator(
    randomNumberGenerator,
    numberValueGenerator,
    productValueGenerator,
    "none",
  ),
  // a / (b + c)
  new DivisionExpressionValueGenerator(
    randomNumberGenerator,
    numberValueGenerator,
    sumValueGenerator,
    "right",
  ),
  // (a + b) / c
  new DivisionExpressionValueGenerator(
    randomNumberGenerator,
    sumValueGenerator,
    numberValueGenerator,
    "left",
  ),
  // a / (b - c)
  new DivisionExpressionValueGenerator(
    randomNumberGenerator,
    numberValueGenerator,
    subtractionValueGenerator,
    "right",
  ),
  // (a - b) / c
  new DivisionExpressionValueGenerator(
    randomNumberGenerator,
    subtractionValueGenerator,
    numberValueGenerator,
    "left",
  ),
]
const fighterGenerator: FighterGenerator = new FighterGenerator(
  [
    numberValueGenerator,
    sumValueGenerator,
    subtractionValueGenerator,
    divisionValueGenerator,
    productValueGenerator,
    factorialValueGenerator,
    constantValueGenerator,
    new EquationValueGenerator(randomNumberGenerator, [
      sumEquationValueGenerator,
      subtractionEquationValueGenerator,
      productEquationValueGenerator,
      divisionEquationValueGenerator,
    ]),
    new ExpressionValueGenerator(
      randomNumberGenerator,
      expressionValueGenerators,
    ),
  ],
  randomNumberGenerator,
)
export const fightResolver: FightResolver = new FightResolver(fighterGenerator)
const numberGenerator: NumberGenerator = new NumberGenerator(
  randomNumberGenerator,
  logger,
)
export const gameGenerator: GameGenerator = new GameGenerator(
  randomNumberGenerator,
  fighterGenerator,
  numberGenerator,
  logger,
)

export const gameProvider = new GameProvider(
  gameGenerator,
  randomNumberGenerator,
)

export const deserializer: GameDeserializer = new GameDeserializer([
  new BracketedValueDeserializer(),
  new DivisionValueDeserializer(),
  new NumberValueDeserializer(),
  new ProductValueDeserializer(),
  new FactorialValueDeserializer(),
  new ConstantValueDeserializer(),
  new SubtractionValueDeserializer(),
])
